import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography } from '@mui/material';

// ── Module-level crop cache ──────────────────────────────────────────────────
// Keyed by `${lobbyId}::${hotspotId}::${bgUrl}::${canvasW}x${canvasH}`.
const CROP_CACHE    = new Map();
const CROP_INFLIGHT = new Map();

// Cache for decoded source media (img/video elements)
const BG_MEDIA_CACHE = new Map();

// Determine if a URL is a video by extension or content.
function isVideoUrl(url) {
  if (!url) return false;
  const u = url.split('?')[0].toLowerCase();
  return /\.(mp4|webm|ogg|ogv|mov|m4v)(\b|$)/.test(u);
}

// Load any background URL into a drawable source element.
// Returns Promise<{el, natW, natH}> where el is an <img> or <video>.
function loadMediaFrame(url, bgType) {
  if (!url) return Promise.reject(new Error('no url'));
  const cacheKey = url;
  if (BG_MEDIA_CACHE.has(cacheKey)) return Promise.resolve(BG_MEDIA_CACHE.get(cacheKey));

  const isVid = bgType === 'video' || isVideoUrl(url);

  if (isVid) {
    return new Promise((resolve, reject) => {
      const vid = document.createElement('video');
      vid.crossOrigin = 'anonymous';
      vid.muted       = true;
      vid.playsInline = true;
      vid.preload     = 'metadata';

      const capture = () => {
        const natW = vid.videoWidth;
        const natH = vid.videoHeight;
        if (!natW || !natH) { reject(new Error('video has no dimensions')); return; }
        const result = { el: vid, natW, natH };
        BG_MEDIA_CACHE.set(cacheKey, result);
        resolve(result);
      };

      vid.addEventListener('seeked',      capture, { once: true });
      vid.addEventListener('loadeddata',  () => {
        // Seek to 1s (or 10% of duration) for a representative frame
        const seekTo = isFinite(vid.duration) && vid.duration > 0
          ? Math.min(1, vid.duration * 0.1)
          : 0;
        if (seekTo > 0 && seekTo !== vid.currentTime) {
          vid.currentTime = seekTo;
          // 'seeked' event will fire capture
        } else {
          capture();
        }
      }, { once: true });
      vid.addEventListener('error', () => reject(new Error('video load failed')), { once: true });

      vid.src = url;
      vid.load();
    });
  }

  // Image (including GIFs — canvas draws the frame at load time)
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload  = () => {
      const result = { el: img, natW: img.naturalWidth, natH: img.naturalHeight };
      BG_MEDIA_CACHE.set(cacheKey, result);
      resolve(result);
    };
    img.onerror = () => reject(new Error('image load failed'));
    img.src = url;
  });
}

// ── Compute hotspot bounding box in canvas-fraction space (0..1) ──────────────
// All coordinate systems:
//   rect   → points[0] is top-left (x%, y%), meta.width/height are % of canvas
//   circle → points[0] is centre (x%, y%), meta.radius is % of canvas width
//   polygon/bezier → points[] are corner positions (x%, y%)
//
// Returns { cx, cy, w, h } all in 0..1 fractions of canvas dimensions.
function getHotspotBBoxFrac(h) {
  if (!h) return null;

  if (h.shape === 'rect' && h.points?.[0] && h.meta) {
    const x  = h.points[0].x / 100;
    const y  = h.points[0].y / 100;
    const w  = (h.meta.width  || 10) / 100;
    const hh = (h.meta.height || 10) / 100;
    return { cx: x + w / 2, cy: y + hh / 2, w, h: hh };
  }

  if (h.shape === 'circle' && h.points?.[0]) {
    const r = (h.meta?.radius || 5) / 100;
    return { cx: h.points[0].x / 100, cy: h.points[0].y / 100, w: r * 2, h: r * 2 };
  }

  // Polygon or bezier — use points[] array (percentages)
  const pts = h.points || [];
  if (pts.length < 1) return null;
  const xs = pts.map(p => p.x / 100), ys = pts.map(p => p.y / 100);
  const minX = Math.min(...xs), maxX = Math.max(...xs);
  const minY = Math.min(...ys), maxY = Math.max(...ys);
  return {
    cx: (minX + maxX) / 2,
    cy: (minY + maxY) / 2,
    w:  maxX - minX,
    h:  maxY - minY,
  };
}

// ── Map canvas-fraction coordinate to exact image-pixel crop rect ─────────────
// Returns { sx, sy, cropW, cropH } — the exact rectangle in native image pixels
// that corresponds to the hotspot's bounding box as rendered via objectFit:cover.
function canvasFracToImageRect(fracCX, fracCY, fracW, fracH, canvasW, canvasH, imgNatW, imgNatH) {
  // objectFit: cover scale
  const scale = Math.max(canvasW / imgNatW, canvasH / imgNatH);

  // Rendered image size and centred-crop offsets
  const rendW = imgNatW * scale;
  const rendH = imgNatH * scale;
  const offX  = (rendW - canvasW) / 2;
  const offY  = (rendH - canvasH) / 2;

  // Hotspot dimensions in native image pixels (exact, no padding)
  const cropW = Math.max((fracW * canvasW) / scale, 1);
  const cropH = Math.max((fracH * canvasH) / scale, 1);

  // Enforce a sensible minimum so tiny hotspots (pulse dots, small circles)
  // produce a visible crop rather than a 2×3 px smear. Minimum is 8% of the
  // image's shorter edge, which keeps the thumbnail readable while still being
  // clearly anchored to the hotspot area.
  const minPx = Math.min(imgNatW, imgNatH) * 0.08;
  const w = Math.max(cropW, minPx);
  const h = Math.max(cropH, minPx);

  // Hotspot top-left in native image pixels (from centre)
  const imgPxCX = (fracCX * canvasW + offX) / scale;
  const imgPxCY = (fracCY * canvasH + offY) / scale;
  let sx = imgPxCX - w / 2;
  let sy = imgPxCY - h / 2;

  // Clamp inside image bounds
  sx = Math.max(0, Math.min(sx, imgNatW - w));
  sy = Math.max(0, Math.min(sy, imgNatH - h));

  return { sx, sy, cropW: w, cropH: h };
}

// ── Crop the exact hotspot area from the bg ───────────────────────────────────
// Returns a Promise<{ dataUrl, aspectRatio }>.
// Supports image, GIF, and video backgrounds.
async function cropHotspotRegion(bgUrl, bgType, hotspot, lobbyId, canvasW, canvasH) {
  if (!bgUrl || !hotspot) throw new Error('missing bg or hotspot');
  const cacheKey = `${lobbyId || 'x'}::${hotspot.id || hotspot._id}::${bgUrl}::${canvasW}x${canvasH}`;
  if (CROP_CACHE.has(cacheKey))    return CROP_CACHE.get(cacheKey);
  if (CROP_INFLIGHT.has(cacheKey)) return CROP_INFLIGHT.get(cacheKey);

  const promise = (async () => {
    const { el, natW: imgW, natH: imgH } = await loadMediaFrame(bgUrl, bgType);
    if (!imgW || !imgH) throw new Error('media has no dimensions');

    const bbox = getHotspotBBoxFrac(hotspot);
    if (!bbox) throw new Error('hotspot has no bbox');

    const { sx, sy, cropW, cropH } = canvasFracToImageRect(
      bbox.cx, bbox.cy, bbox.w, bbox.h,
      canvasW, canvasH, imgW, imgH
    );

    const maxOut   = 480;
    const outScale = Math.min(1, maxOut / Math.max(cropW, cropH));
    const outW     = Math.round(cropW * outScale);
    const outH     = Math.round(cropH * outScale);

    const canvas  = document.createElement('canvas');
    canvas.width  = outW;
    canvas.height = outH;
    canvas.getContext('2d').drawImage(el, sx, sy, cropW, cropH, 0, 0, outW, outH);

    const dataUrl     = canvas.toDataURL('image/jpeg', 0.88);
    const aspectRatio = cropW / cropH;
    const result      = { dataUrl, aspectRatio };
    CROP_CACHE.set(cacheKey, result);
    return result;
  })().finally(() => CROP_INFLIGHT.delete(cacheKey));

  CROP_INFLIGHT.set(cacheKey, promise);
  return promise;
}

// ── Hook: returns { dataUrl, aspectRatio, loading, error } ───────────────────
function useHotspotCrop(bgUrl, bgType, hotspot, lobbyId, canvasW, canvasH, enabled) {
  const [state, setState] = useState({ dataUrl: null, aspectRatio: 1, loading: enabled, error: null });
  const hotspotId = hotspot?.id || hotspot?._id || null;

  useEffect(() => {
    if (!enabled || !bgUrl || !hotspot) {
      setState({ dataUrl: null, aspectRatio: 1, loading: false, error: null });
      return;
    }
    const cacheKey = `${lobbyId || 'x'}::${hotspotId}::${bgUrl}::${canvasW}x${canvasH}`;
    if (CROP_CACHE.has(cacheKey)) {
      const cached = CROP_CACHE.get(cacheKey);
      setState({ dataUrl: cached.dataUrl, aspectRatio: cached.aspectRatio, loading: false, error: null });
      return;
    }
    let cancelled = false;
    setState({ dataUrl: null, aspectRatio: 1, loading: true, error: null });
    cropHotspotRegion(bgUrl, bgType, hotspot, lobbyId, canvasW, canvasH)
      .then(result => { if (!cancelled) setState({ dataUrl: result.dataUrl, aspectRatio: result.aspectRatio, loading: false, error: null }); })
      .catch(err   => { if (!cancelled) setState({ dataUrl: null, aspectRatio: 1, loading: false, error: err }); });
    return () => { cancelled = true; };
  }, [bgUrl, bgType, hotspotId, lobbyId, canvasW, canvasH, enabled]);

  return state;
}

// ── Spinner shown while a crop is computing ──────────────────────────────────
function Spinner({ size = 28 }) {
  return (
    <Box
      sx={{
        width: size, height: size, borderRadius: '50%',
        border: '2.5px solid rgba(0,0,0,0.08)',
        borderTopColor: 'rgba(0,0,0,0.55)',
        animation: 'mhd-spin 0.8s linear infinite',
        '@keyframes mhd-spin': { to: { transform: 'rotate(360deg)' } },
      }}
    />
  );
}

// ── Card icon (used when no image is available / cropping failed) ────────────
function CardIcon({ type, color = '#5a6474', size = 56 }) {
  const props = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: color, strokeWidth: 1.6, strokeLinecap: 'round', strokeLinejoin: 'round' };
  switch (type) {
    case 'link-external':
      return (<svg {...props}><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>);
    case 'link-lobby':
      return (<svg {...props}><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><path d="M9 22V12h6v10" /></svg>);
    case 'chat':
      return (<svg {...props}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>);
    case 'gallery':
      return (<svg {...props}><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="M21 15l-5-5L5 21" /></svg>);
    case 'iframe':
      return (<svg {...props}><rect x="2" y="6" width="14" height="14" rx="2" /><path d="M8 6V4a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-2" /></svg>);
    case 'video':
    case 'livestream':
      return (<svg {...props}><polygon points="23 7 16 12 23 17 23 7" /><rect x="1" y="5" width="15" height="14" rx="2" /></svg>);
    default:
      return null;
  }
}

// ── Action icon shown in the top-right of each hotspot card ──────────────────
// Maps the card's intent to a recognisable glyph at action-icon size.
// link / link-external → arrow
// video / livestream    → play triangle
// gallery               → gallery (image)
// chat                  → chat bubble
// iframe                → popup (two overlapping rectangles)
function ActionIcon({ intent, color = '#0a0e1a', size = 24 }) {
  const stroke = {
    width: size, height: size, viewBox: '0 0 24 24',
    fill: 'none', stroke: color, strokeWidth: 2.25,
    strokeLinecap: 'round', strokeLinejoin: 'round',
  };
  switch (intent) {
    case 'link':
    case 'link-external':
      return (
        <svg {...stroke}>
          <line x1="5" y1="12" x2="19" y2="12" />
          <polyline points="12 5 19 12 12 19" />
        </svg>
      );
    case 'video':
    case 'livestream':
      // Solid play triangle — friendlier than an outlined polygon at small size.
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
          <path d="M7 5.5v13a1 1 0 0 0 1.55.83l10-6.5a1 1 0 0 0 0-1.66l-10-6.5A1 1 0 0 0 7 5.5z" />
        </svg>
      );
    case 'chat':
      return (
        <svg {...stroke}>
          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
        </svg>
      );
    case 'gallery':
      return (
        <svg {...stroke}>
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <circle cx="8.5" cy="8.5" r="1.5" />
          <path d="M21 15l-5-5L5 21" />
        </svg>
      );
    case 'iframe':
      // Two overlapping rounded rectangles = "popup / open in window"
      return (
        <svg {...stroke}>
          <rect x="3" y="7" width="13" height="13" rx="2" />
          <path d="M8 7V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-2" />
        </svg>
      );
    default:
      return (
        <svg {...stroke}>
          <line x1="5" y1="12" x2="19" y2="12" />
          <polyline points="12 5 19 12 12 19" />
        </svg>
      );
  }
}

// ── Hero card (bg image on top + lobby name below) ───────────────────────────
function HeroCard({ lobbyName }) {
  return (
    <Box>
      <Typography
        sx={{
          fontSize: '0.95rem', fontWeight: 500, color: '#5a6474',
          lineHeight: 1.2, mb: 0.5, letterSpacing: '0.005em',
        }}
      >
        Welcome to
      </Typography>
      <Typography
        sx={{
          fontSize: '2rem', fontWeight: 800, color: '#0a0e1a',
          lineHeight: 1.05, letterSpacing: '-0.015em',
          overflow: 'hidden', textOverflow: 'ellipsis',
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          wordBreak: 'break-word',
        }}
      >
        {lobbyName || 'Lobby'}
      </Typography>
    </Box>
  );
}

// ── Hotspot card ─────────────────────────────────────────────────────────────
// White card. The cropped thumbnail sits in the upper-left area with a small
// white frame + soft shadow. The action icon (arrow/play/gallery/chat/popup)
// is absolutely positioned in the top-right corner. The title runs along the
// bottom of the card.
function HotspotCard({
  card,
  currentLobbyBgUrl,
  currentLobbyBgType,
  currentLobbyId,
  canvasW,
  canvasH,
  onClick,
}) {
  // Cropping works for image, GIF (treated as image), and video (frame capture).
  // Only skip when there's no URL at all.
  const cropEnabled = !!currentLobbyBgUrl;
  const crop = useHotspotCrop(currentLobbyBgUrl, currentLobbyBgType, card.hotspot, currentLobbyId, canvasW, canvasH, cropEnabled);

  const thumbUrl         = crop.dataUrl;
  const showSpinner      = cropEnabled && crop.loading;
  const showIconFallback = !thumbUrl && !showSpinner;

  return (
    <Box
      onClick={onClick}
      sx={{
        position: 'relative',
        width: '100%',
        // No fixed aspect ratio — the card sizes itself to its content
        // (thumbnail + gap + 2-line title area). This way 2-line and 1-line
        // titles produce equal-height cards, and increasing the thumbnail
        // size scales the card up rather than overflowing inside a fixed box.
        borderRadius: '22px',
        background: '#ffffff',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 8px 24px rgba(0,0,0,0.10)',
        cursor: 'pointer',
        WebkitTapHighlightColor: 'transparent',
        transition: 'transform 0.18s ease, box-shadow 0.18s ease',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        p: 2,
        '&:active': {
          transform: 'scale(0.98)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 4px 12px rgba(0,0,0,0.08)',
        },
      }}
    >
      {/* Top row: thumbnail (left) + action icon (top-right) ──────────────── */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          width: '100%',
        }}
      >
        {/* Thumbnail — fixed square, image set to contain so the full
            hotspot crop is visible without distortion or extra cropping. */}
        <Box
          sx={{
            width: '70%',
            aspectRatio: '1 / 1',
            borderRadius: '14px',
            overflow: 'hidden',
            background: 'rgba(244,247,252,0.95)',
            border: '3px solid #ffffff',
            boxShadow: '0 6px 18px rgba(0,0,0,0.18), 0 1px 3px rgba(0,0,0,0.08)',
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            position: 'relative',
          }}
        >
          {showSpinner && <Spinner size={28} />}
          {showIconFallback && <CardIcon type={card.iconType} color="#5a6474" size={40} />}
          {thumbUrl && (
            <Box
              component="img"
              src={thumbUrl}
              alt=""
              draggable={false}
              sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
            />
          )}
          {/* Hotspot-coloured dot — centred on the thumbnail, 50% the size
              of the canvas pulse dot (DOT_R=10.5 → diameter≈21px → 50%≈10px).
              Fill and ring colour match the hotspot's glowColor/borderColor. */}
          {thumbUrl && (() => {
            const dotColor = card.hotspot?.hoverBorderColor || card.hotspot?.glowColor || '#1075BA';
            return (
              <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', pointerEvents: 'none' }}>
                {/* Expanding ring */}
                <Box sx={{
                  position: 'absolute', top: '50%', left: '50%',
                  width: 10, height: 10,
                  borderRadius: '50%',
                  border: `1.5px solid ${dotColor}`,
                  boxSizing: 'border-box',
                  transform: 'translate(-50%, -50%)',
                  animation: 'mhd-dot-ring 2s ease-out 0.4s infinite',
                  '@keyframes mhd-dot-ring': {
                    '0%':   { transform: 'translate(-50%, -50%) scale(1)', opacity: 0.7 },
                    '100%': { transform: 'translate(-50%, -50%) scale(2.8)', opacity: 0 },
                  },
                }} />
                {/* Solid dot */}
                <Box sx={{
                  position: 'relative',
                  width: 10, height: 10,
                  borderRadius: '50%',
                  background: dotColor,
                  border: '1.5px solid #ffffff',
                  boxSizing: 'border-box',
                  boxShadow: `0 0 6px ${dotColor}99`,
                  animation: 'mhd-dot-pulse 2s ease-in-out infinite',
                  '@keyframes mhd-dot-pulse': {
                    '0%,100%': { transform: 'scale(1)' },
                    '50%':     { transform: 'scale(1.28)' },
                  },
                }} />
              </Box>
            );
          })()}
        </Box>

        {/* Action icon — top-right */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mt: 0.5,
            mr: 0.25,
          }}
        >
          <ActionIcon intent={card.intent} color="#0a0e1a" size={26} />
        </Box>
      </Box>

      {/* Gap between image and label ─────────────────────────────────────── */}
      <Box sx={{ height: 18, flexShrink: 0 }} />

      {/* Title — clamped to 2 lines, with vertical space reserved for the
          second line so 1-line and 2-line cards align consistently. */}
      <Typography
        sx={{
          fontSize: '1.05rem',
          fontWeight: 800,
          color: '#0a0e1a',
          lineHeight: 1.2,
          letterSpacing: '-0.01em',
          // Clamp to 2 lines with ellipsis on overflow
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          wordBreak: 'break-word',
          // Reserve the height of 2 lines even when the title is 1 line, so
          // all cards in a row have equal height regardless of title length.
          minHeight: 'calc(1.2em * 2)',
        }}
      >
        {card.title}
      </Typography>
    </Box>
  );
}

// ── Build the card list from resolved hotspots ───────────────────────────────
function buildCards(resolvedHotspots, siblings, hasStream, streamHotspot) {
  const cards = [];
  const siblingsById = new Map((siblings || []).map(s => [String(s._id), s]));

  for (const h of resolvedHotspots) {
    if (!h.isActive) continue;
    const t = h.hotspotType;
    const action = h.hotspotAction || {};
    const label = h.label || '';

    if (t === 'link') {
      if (action.linkTarget === 'external' && action.externalUrl) {
        cards.push({ key: h.id, hotspot: h, iconType: 'link-external', coverUrl: null, title: label ? `Go to ${label}` : 'Open Link', intent: 'link-external' });
      } else if (action.targetId) {
        const target = siblingsById.get(String(action.targetId));
        const targetName = target?.name || label || 'Lobby';
        const targetBg = target?.bgUrl || null;
        cards.push({ key: h.id, hotspot: h, iconType: 'link-lobby', coverUrl: targetBg, title: `Go to ${targetName}`, intent: 'link' });
      }
    } else if (t === 'image') {
      const ia = h.hotspotAction || {};
      if (ia.linkTarget === 'external' && ia.externalUrl) {
        cards.push({ key: h.id, hotspot: h, iconType: 'link-external', coverUrl: null, title: label ? `Go to ${label}` : 'Open Link', intent: 'link-external' });
      } else if (ia.targetId) {
        const target = siblingsById.get(String(ia.targetId));
        const targetName = target?.name || label || 'Lobby';
        const targetBg = target?.bgUrl || null;
        cards.push({ key: h.id, hotspot: h, iconType: 'link-lobby', coverUrl: targetBg, title: `Go to ${targetName}`, intent: 'link' });
      }
    } else if (t === 'chat' && action.channelId) {
      cards.push({ key: h.id, hotspot: h, iconType: 'chat', coverUrl: null, title: action.chatTitle || label || 'Chat', intent: 'chat' });
    } else if (t === 'gallery' && action.images?.length) {
      cards.push({ key: h.id, hotspot: h, iconType: 'gallery', coverUrl: null, title: label || 'Gallery', intent: 'gallery' });
    } else if (t === 'iframe' && action.embedCode) {
      cards.push({ key: h.id, hotspot: h, iconType: 'iframe', coverUrl: null, title: label || 'Popup', intent: 'iframe' });
    } else if (t === 'video' && action.videoUrl) {
      cards.push({ key: h.id, hotspot: h, iconType: 'video', coverUrl: null, title: label ? `Watch ${label}` : 'Watch Video', intent: 'video' });
    }
  }

  if (hasStream && streamHotspot) {
    cards.push({
      key: streamHotspot.id || 'livestream',
      hotspot: streamHotspot,
      iconType: 'livestream',
      coverUrl: null,
      title: streamHotspot.label ? `Enter ${streamHotspot.label}` : 'Enter Livestream',
      intent: 'livestream',
    });
  }

  return cards;
}

// ── Animated scroll-down launcher button ─────────────────────────────────────
function LauncherButton({ onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        width: 48,
        height: 48,
        borderRadius: '50%',
        background: '#ffffff',
        boxShadow: '0 4px 18px rgba(0,0,0,0.22), 0 1px 3px rgba(0,0,0,0.08)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        WebkitTapHighlightColor: 'transparent',
        // Bobbing animation: gently floats up and down in a loop.
        animation: 'mhd-bob 1.8s ease-in-out infinite',
        '@keyframes mhd-bob': {
          '0%,100%': { transform: 'translateY(0)' },
          '50%':     { transform: 'translateY(-7px)' },
        },
        '&:active': {
          animation: 'none',
          transform: 'scale(0.93)',
        },
      }}
    >
      <svg
        viewBox="0 0 24 24"
        width="22"
        height="22"
        fill="none"
        stroke="#0a0e1a"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polyline points="6 9 12 15 18 9" />
      </svg>
    </Box>
  );
}

export function MobileHotspotDrawer({
  lobby,
  bgUrl,
  bgType,
  canvasW = 375,
  canvasH = 700,
  siblings,
  resolvedHotspots,
  hasStream,
  streamHotspot,
  onHotspotClick,
  openChatWindow,
  onEnterLivestream,
  isMobile,
  // launcherOnly: render only the floating "All Activities" pill (used
  //               inside the canvas container so it overlays the canvas)
  launcherOnly = false,
  cardsOnly = false,
  // Called with true/false when launcherOnly mode mounts/unmounts,
  // so the parent can offset the showcase button above it.
  onHasCards,
}) {
  // Ref to the card section — the launcher scrolls to this element.
  // When launcherOnly=true we can't ref the section (different render),
  // so we use a stable ID-based scroll instead.
  const SECTION_ID = `mhd-cards-${lobby?._id || 'lobby'}`;

  const cards = React.useMemo(
    () => buildCards(resolvedHotspots || [], siblings || [], hasStream, streamHotspot),
    [resolvedHotspots, siblings, hasStream, streamHotspot]
  );

  const scrollToSection = useCallback(() => {
    const el = document.getElementById(SECTION_ID);
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [SECTION_ID]);

  const onCardClick = useCallback((card) => {
    if (card.intent === 'chat') {
      openChatWindow?.(card.hotspot);
    } else if (card.intent === 'livestream') {
      onEnterLivestream?.();
    } else {
      onHotspotClick?.(card.hotspot);
    }
  }, [onHotspotClick, openChatWindow, onEnterLivestream]);

  // Notify the parent whether a launcher is actually rendered so it can
  // offset the showcase button above it. Run on every cards/lobby change.
  React.useLayoutEffect(() => {
    if (!launcherOnly) return;
    const visible = !!(isMobile && lobby && cards.length > 0);
    onHasCards?.(visible);
    return () => onHasCards?.(false);
  }, [launcherOnly, isMobile, lobby, cards.length, onHasCards]);

  if (!isMobile || !lobby) return null;
  // In cardsOnly mode we always render at minimum the HeroCard (welcome banner),
  // even when there are no hotspot cards. The launcher pill is only meaningful
  // when there are cards to scroll to, so it still bails on empty.
  if (cards.length === 0 && !cardsOnly) return null;

  // ── Launcher-only mode ──────────────────────────────────────────────────
  if (launcherOnly) {
    return (
      <Box
        sx={{
          position: 'absolute',
          right: 16,
          bottom: 24,
          zIndex: 9996,
          pointerEvents: 'all',
        }}
      >
        <LauncherButton onClick={scrollToSection} />
      </Box>
    );
  }

  // ── Cards-only mode — normal page flow below the canvas ─────────────────
  if (cardsOnly) {
    return (
      <Box
        id={SECTION_ID}
        sx={{
          width: '100%',
          background: '#f4f7fc',
          px: 2,
          pt: 2.5,
          pb: 9,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.75,
        }}
      >
        <HeroCard lobbyName={lobby?.name} />
        {cards.length > 0 && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 1.75,
            }}
          >
            {cards.map(card => (
              <HotspotCard
                key={card.key}
                card={card}
                currentLobbyBgUrl={bgUrl}
                currentLobbyBgType={bgType}
                currentLobbyId={lobby?._id}
                canvasW={canvasW}
                canvasH={canvasH}
                onClick={() => onCardClick(card)}
              />
            ))}
          </Box>
        )}
      </Box>
    );
  }

  return null;
}

export default MobileHotspotDrawer;